import { useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { websiteAuthService } from "@/api/websiteAuthService"
import { PasswordInput } from "../components/PasswordField"

export const ResetPassword = () => {
  const [params] = useSearchParams()
  const email = params.get("email") || ""
  const token = params.get("token") || ""
  const [password, setPassword] = useState("")
  const [confirmation, setConfirmation] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState("")
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError("")
    if (password !== confirmation) {
      setError("The passwords do not match.")
      return
    }
    setIsSaving(true)
    try {
      await websiteAuthService.resetPassword({
        email,
        token,
        password,
        password_confirmation: confirmation,
      })
      localStorage.removeItem("website_token")
      localStorage.removeItem("customer")
      queryClient.removeQueries({ queryKey: ["cart"] })
      queryClient.removeQueries({ queryKey: ["orders"] })
      toast.success(
        "Password reset successfully. Please sign in with your new password."
      )
      navigate("/login", { replace: true })
    } catch (error: any) {
      setError(
        error?.error?.password?.[0] ||
          error?.error?.token?.[0] ||
          error?.error?.email?.[0] ||
          error?.message ||
          "Unable to reset your password. Please try again."
      )
    } finally {
      setIsSaving(false)
    }
  }

  const validLink = !!email && !!token

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-6 bg-white p-8 rounded-xl shadow-md">
        <h1 className="text-3xl font-bold text-gray-900">Reset password</h1>
        {!validLink ? (
          <p role="alert" className="text-sm text-red-700">
            This reset link is incomplete. Please request a new link.
          </p>
        ) : (
          <>
            <p className="break-words text-sm text-gray-600">
              Choose a new password for {email}.
            </p>
            {error && (
              <p
                role="alert"
                className="rounded-md bg-red-50 p-3 text-sm text-red-700"
              >
                {error}
              </p>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="reset-password"
                  className="block text-sm font-medium text-gray-700"
                >
                  New password
                </label>
                <PasswordInput
                  id="reset-password"
                  visibilityLabel="new password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  disabled={isSaving}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  At least 8 characters.
                </p>
              </div>
              <div>
                <label
                  htmlFor="reset-password-confirmation"
                  className="block text-sm font-medium text-gray-700"
                >
                  Confirm new password
                </label>
                <PasswordInput
                  id="reset-password-confirmation"
                  visibilityLabel="password confirmation"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  disabled={isSaving}
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <button
                type="submit"
                disabled={isSaving}
                className="w-full rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {isSaving ? "Saving..." : "Reset password"}
              </button>
            </form>
          </>
        )}
        <Link
          to="/forgot-password"
          className="block text-center text-sm text-indigo-600 hover:text-indigo-500"
        >
          Request a new reset link
        </Link>
        <Link
          to="/login"
          className="block text-center text-sm text-indigo-600 hover:text-indigo-500"
        >
          Back to login
        </Link>
      </div>
    </div>
  )
}
